import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import UserBadge from './UserBadge';
import './style-new.css';

import {
  FaEye,
  FaTrash,
  FaEdit,
  FaUsers,
  FaPlus,
  FaUsersCog,
  FaHome,
  FaFutbol
} from 'react-icons/fa';

import { MdCancel } from 'react-icons/md';

const CreateGame = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const preselectedGroupId = location.state?.group_id || '';
  const preselectedGroupName = location.state?.group_name || '';

  const [formData, setFormData] = useState({
    description: '',
    game_date: '',
    game_time: '',
    venue: '',
    cost: '',
    opponent: '',
    colour: '',
    notes: '',
    group_id: preselectedGroupId,
    max_players: '',
    rsvp_cutoff: '',
  });

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isGroupSelectDisabled, setIsGroupSelectDisabled] = useState(!!preselectedGroupId);

useEffect(() => {
  const fetchAdminGroups = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('No token found');

      const response = await fetch('/api/groups/my-groups-with-admin-status', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to fetch groups with admin status');

      const data = await response.json();
      const allGroups = Array.isArray(data.groups) ? data.groups : [];

      // Filter only groups where user is admin
      const adminGroups = allGroups.filter(group => group.is_admin);

      if (adminGroups.length === 0) {
        alert('You are not an admin of any groups. Please create a group to create a game.');
        navigate('/groups/create-group');
        return;
      }

      setGroups(adminGroups);

      if (!preselectedGroupId) {
        if (adminGroups.length === 1) {
          setFormData(prev => ({ ...prev, group_id: adminGroups[0].id }));
          setIsGroupSelectDisabled(true);
        } else {
          setIsGroupSelectDisabled(false);
        }
      }
    } catch (err) {
      console.error('Error fetching groups with admin status:', err);
      alert('Unable to load groups. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  fetchAdminGroups();
}, [navigate, preselectedGroupId]);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { group_id, max_players, rsvp_cutoff, game_date, game_time, description } = formData;

    if ((!isGroupSelectDisabled && !group_id) || !description || !game_date || !game_time || !max_players) {
      alert('Please fill in all mandatory fields: Group, Description, Date, Time, Max Players.');
      return;
    }

    if (parseInt(max_players) <= 0) {
      alert('Please specify a valid number of players.');
      return;
    }

    const [year, month, day] = formData.game_date.split('-').map(Number);
    const [hour, minute] = formData.game_time.split(':').map(Number);
    const selectedDateTime = new Date(year, month - 1, day, hour, minute);

    if (selectedDateTime <= new Date()) {
      alert('You cannot create a game in the past.');
      return;
    }

    const gameData = {
      ...formData,
      game_datetime: selectedDateTime.toISOString(),
      max_players: parseInt(max_players),
      rsvp_cutoff: parseInt(rsvp_cutoff) >= 0 ? parseInt(rsvp_cutoff) : 0,
    };

    try {
      const response = await fetch('/api/games', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        },
        body: JSON.stringify(gameData),
      });

      if (!response.ok) {
        const err = await response.json();
        alert(`Error: ${err.error}`);
        return;
      }

      alert('Game created successfully!');
      setTimeout(() => navigate('/games'), 1000);
    } catch (err) {
      console.error('Error creating game:', err);
      alert('Failed to create game.');
    }
  };

  const getTodayDate = () => new Date().toISOString().split('T')[0];

  const getCurrentTime = () => {
    const now = new Date();
    now.setSeconds(0, 0);
    return now.toTimeString().slice(0, 5);
  };

  const generateTimeOptions = () => {
    const times = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 15) {
        const hour = h.toString().padStart(2, '0');
        const minute = m.toString().padStart(2, '0');
        const time = `${hour}:${minute}`;
        const hourNum = parseInt(hour);
        if (hourNum >= 7 && hourNum <= 22) {
          times.push(time);
        }
      }
    }
    return times;
  };

  if (loading) return <p>Loading...</p>;

  return (
    <div className="form-container">
      <UserBadge />
      <h2>Create Game</h2>
      <form onSubmit={handleSubmit}>

<select
  name="group_id"
  value={formData.group_id}
  onChange={handleChange}
  required={!isGroupSelectDisabled}
  disabled={isGroupSelectDisabled}
  className="input-field"
>
  <option value="" disabled hidden className="placeholder-option">
    {isGroupSelectDisabled && preselectedGroupName
      ? `Group: ${preselectedGroupName}`
      : 'Select Group *'}
  </option>
  {groups.map((group) => (
    <option key={group.id} value={group.id}>
      {group.name}
    </option>
  ))}
</select>



        <input
          type="text"
          name="description"
          placeholder="Name or Description *"
          value={formData.description}
          onChange={handleChange}
          required
          className="input-field"
        />

<input
  type="date"
  name="game_date"
  min={getTodayDate()}
  value={formData.game_date}
  onChange={handleChange}
  required
  className="input-field"
/>

        <select
          name="game_time"
          value={formData.game_time}
          onChange={handleChange}
          required
          className="input-field"
        >
          <option value="">
            Select Game Time
            <span className="required-asterisk">*</span>
          </option>
          {generateTimeOptions().map((time) => {
            const isToday = formData.game_date === getTodayDate();
            const isPast = isToday && time < getCurrentTime();
            return (
              <option key={time} value={time} disabled={isPast}>
                {time}
              </option>
            );
          })}
        </select>

        <input
          type="text"
          name="venue"
          placeholder="Venue"
          value={formData.venue}
          onChange={handleChange}
          className="input-field"
        />

        <input
          type="number"
          name="cost"
          placeholder="Cost per person"
          value={formData.cost}
          onChange={handleChange}
          min="0"
          step="0.01"
          onWheel={(e) => e.target.blur()}
          className="input-field"
        />

        <input
          type="text"
          name="opponent"
          placeholder="Opponent"
          value={formData.opponent}
          onChange={handleChange}
          className="input-field"
        />

        <input
          type="text"
          name="colour"
          placeholder="Colours"
          value={formData.colour}
          onChange={handleChange}
          className="input-field"
        />

        <input
          type="text"
          name="notes"
          placeholder="Notes"
          value={formData.notes}
          onChange={handleChange}
          className="input-field"
        />

        <input
          type="number"
          name="max_players"
          placeholder="Max Players *"
          value={formData.max_players}
          onChange={handleChange}
          min="1"
          required
          className="input-field"
          onWheel={(e) => e.target.blur()}
        />

        <label htmlFor="rsvp_cutoff" className="label-small">
          RSVP OUT Cutoff Time (hours before game):
        </label>
        <select
          name="rsvp_cutoff"
          id="rsvp_cutoff"
          value={formData.rsvp_cutoff}
          onChange={handleChange}
          className="input-field"
        >
          <option value="" disabled hidden>
            Select hours
          </option>
          {[...Array(20)].map((_, i) => (
            <option key={i + 1} value={i + 1}>
              {i + 1} hour{i === 0 ? '' : 's'}
            </option>
          ))}
        </select>

        <input type="hidden" name="rsvp_cutoff" value={formData.rsvp_cutoff || ''} />


<div className="button-group" style={{ marginTop: '10px' }}>
  <button
    type="submit"
    className="btn btn-primary"
    style={{ marginRight: '10px' }}
  >
    <FaPlus />
    Create Game
  </button>
  <button
    type="button"
    onClick={() => navigate(-1)}
    className="btn pastel-red"
  >
    <MdCancel />
    Cancel
  </button>
</div>
      </form>
    </div>
  );
};

export default CreateGame;
